import { useEffect, useMemo, useRef, useState } from 'react'
import { Music, Plus, UploadCloud, Trash2 } from 'lucide-react'
import type { AudioLibraryResponse } from '@shared/types'
import { api } from '../../../api'
import { useEditorStore } from '../../../store/editorStore'
import { Button } from '../../common/Button'
import { CheckboxField, SelectField, SliderField } from '../../common/Field'
import { EmptyState } from '../../common/States'
import { makeId } from '../../../lib/id'
import { assetDisplayName } from '../../../lib/assetName'

/**
 * reviewer 6/10 B-4 (2026-09-06): the earlier version of this hook cached
 * `GET /api/audio/library`'s result in a module variable FOREVER (fetched
 * once per page load, never again) — contradicting the README's own "置け
 * ば使えるようになる" claim (a file dropped into the library dir after the
 * 音タブ was first opened this session would never appear without a full
 * page reload), and a FAILED fetch got cached too (`{dir:'', items:[]}`
 * baked in permanently, so a transient server hiccup looked like "library
 * is empty" forever). Redesigned: every mount (or `reload()` call) re-fetches
 * from the server; `cachedAudioLibrary` now exists ONLY to seed the very
 * first render with the last known-good result (avoiding a "読み込み中"
 * flash on every subsequent 音タブ open this session) — it is a courtesy
 * initial value, never a stand-in for staying fresh. A failed fetch does
 * NOT touch this cache at all, so the NEXT mount/reload tries again from
 * scratch instead of being stuck on a cached failure.
 *
 * `TabAudio`/`AudioInspector` both use this (AudioInspector needs it for
 * builtinId display-name resolution — task spec §3 item 2). Not folded into
 * editorStore.ts (adding a new piece of library state there isn't within
 * this task's authorized editorStore.ts change — only `setSourceAudio` is).
 */
let cachedAudioLibrary: AudioLibraryResponse | null = null

export function useAudioLibrary(): { library: AudioLibraryResponse | null; reload: () => void } {
  const [library, setLibrary] = useState<AudioLibraryResponse | null>(cachedAudioLibrary)
  const [reloadTick, setReloadTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    api.listAudioLibrary().then(
      (res) => {
        cachedAudioLibrary = res
        if (!cancelled) setLibrary(res)
      },
      (e) => {
        // reviewer 6/10 B-4: failure is NOT cached — `cachedAudioLibrary`
        // (and this component's own `library` state) are left exactly as
        // they were, so the next mount/reload retries from scratch rather
        // than being stuck showing a permanent "empty" result.
        console.error('[useAudioLibrary] failed to load /api/audio/library:', e)
      },
    )
    return () => {
      cancelled = true
    }
  }, [reloadTick])

  return { library, reload: () => setReloadTick((t) => t + 1) }
}

const SOURCE_AUDIO_DEFAULT = { enabled: false, gainDb: -12 } as const

export function TabAudio() {
  const composition = useEditorStore((s) => s.composition)
  const assets = useEditorStore((s) => s.assets)
  const addAudio = useEditorStore((s) => s.addAudio)
  const updateAudio = useEditorStore((s) => s.updateAudio)
  const removeAudio = useEditorStore((s) => s.removeAudio)
  const uploadFiles = useEditorStore((s) => s.uploadFiles)
  const setSourceAudio = useEditorStore((s) => s.setSourceAudio)
  const inputRef = useRef<HTMLInputElement>(null)

  const { library, reload: reloadLibrary } = useAudioLibrary()

  // P10-2 担当B §3 item 2: 試聴は同時1本 — 全アイテム共通の <audio> を1つだけ
  // 持ち、再生中の item id を previewingId で追う（同じ id をもう一度押したら
  // 停止＝トグル、別の id を押したら差し替え）。
  const previewAudioRef = useRef<HTMLAudioElement>(null)
  const [previewingId, setPreviewingId] = useState<string | null>(null)

  function togglePreview(id: string) {
    const el = previewAudioRef.current
    if (!el) return
    if (previewingId === id) {
      el.pause()
      setPreviewingId(null)
      return
    }
    el.src = api.fileUrl(`builtin-audio/${id}`)
    el.currentTime = 0
    el.play().catch((err) => console.warn('[TabAudio] preview play() rejected:', err))
    setPreviewingId(id)
  }

  // Audio-only assets ingested into this project (width=0/height=0, ready —
  // server/src/ingest/pipeline.ts skips proxy/thumb/scene-detect for these).
  // "BGMをアップロード" below only *ingests* the file into the asset bin; it
  // never attached it to the audio track (a real gap — a user who uploaded a
  // BGM file that way saw nothing happen once ingest finished). This select
  // is the missing second step: pick an already-ingested audio asset and
  // attach it as a BGM AudioClip.
  const audioAssets = useMemo(
    () => Object.values(assets).filter((a) => a.ingestStatus === 'ready' && a.width === 0 && a.height === 0),
    [assets],
  )
  const [selectedAudioAssetId, setSelectedAudioAssetId] = useState('')

  if (!composition) return null
  const clips = composition.tracks.audio
  const sourceAudio = composition.sourceAudio ?? SOURCE_AUDIO_DEFAULT

  function addAssetBgm() {
    const asset = audioAssets.find((a) => a.id === selectedAudioAssetId) ?? audioAssets[0]
    if (!asset) return
    addAudio({
      id: makeId('aud'),
      kind: 'bgm',
      assetId: asset.id,
      builtinId: null,
      startSec: 0,
      inSec: 0,
      outSec: null,
      gainDb: -8,
      fadeInSec: 0.5,
      fadeOutSec: 1.0,
    })
  }

  function addLibraryBgm(builtinId: string) {
    addAudio({
      id: makeId('aud'),
      kind: 'bgm',
      assetId: null,
      builtinId,
      startSec: 0,
      inSec: 0,
      outSec: null,
      gainDb: -8,
      fadeInSec: 0.5,
      fadeOutSec: 1.0,
    })
  }

  return (
    <div className="p-3 space-y-3">
      <div className="grid grid-cols-1 gap-2">
        <Button variant="secondary" size="sm" icon={<UploadCloud size={13} />} onClick={() => inputRef.current?.click()}>
          BGMをアップロード
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) uploadFiles(Array.from(e.target.files))
            e.target.value = ''
          }}
        />
      </div>

      {audioAssets.length > 0 && (
        <div className="rounded border border-ink-border bg-ink-surface2/50 p-2.5 space-y-2" data-testid="bgm-candidates">
          <div className="text-[11px] font-medium text-text-muted">BGM候補（取込済みの音声素材）</div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <SelectField
                label="素材"
                value={selectedAudioAssetId || audioAssets[0]?.id || ''}
                onChange={(e) => setSelectedAudioAssetId(e.target.value)}
                data-testid="bgm-candidate-select"
              >
                {audioAssets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {assetDisplayName(a)}
                  </option>
                ))}
              </SelectField>
            </div>
            <Button variant="secondary" size="sm" icon={<Plus size={13} />} onClick={addAssetBgm} data-testid="bgm-candidate-add">
              追加
            </Button>
          </div>
        </div>
      )}

      {/* P10-2 担当B §3 item 1/2: BUILTIN_BGM ハードコードを撤去し、GET
          /api/audio/library の一覧に差し替え。 */}
      <div className="rounded border border-ink-border bg-ink-surface2/50 p-2.5 space-y-2" data-testid="audio-library">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-medium text-text-muted">BGMライブラリ</div>
          {/* reviewer 6/10 B-4: フォルダに後から置いたファイルを、音タブを開き
              直さなくても反映できるように。 */}
          <button
            onClick={reloadLibrary}
            className="text-[10px] text-text-faint hover:text-text-muted"
            data-testid="audio-library-reload"
          >
            再読み込み
          </button>
        </div>
        {library === null ? (
          <div className="text-xs text-text-faint">読み込み中…</div>
        ) : library.items.length === 0 ? (
          <div className="text-xs text-text-faint leading-relaxed" data-testid="audio-library-empty">
            BGMフォルダにファイルを置いてください: {library.dir}
          </div>
        ) : (
          <div className="space-y-1.5">
            {library.items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between gap-2 rounded bg-ink-bg/40 px-2 py-1.5"
                data-testid={`audio-library-item-${item.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-xs truncate">{item.title}</div>
                  <div className="text-[10px] text-text-faint tabular-nums">
                    {item.durationSec != null ? `${item.durationSec.toFixed(1)}s` : '尺不明'}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => togglePreview(item.id)}
                    data-testid={`audio-library-preview-${item.id}`}
                  >
                    {previewingId === item.id ? '停止' : '試聴'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => addLibraryBgm(item.id)}
                    data-testid={`audio-library-add-${item.id}`}
                  >
                    BGMにする
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- 試聴専用の非表示 <audio>、キャプション対象コンテンツなし */}
        <audio
          ref={previewAudioRef}
          hidden
          onEnded={() => setPreviewingId(null)}
          data-testid="audio-library-preview-player"
        />
      </div>

      {clips.length === 0 ? (
        <EmptyState icon={<Music size={24} />} title="音声トラックが空です" description="BGMやSFXを追加してください。" />
      ) : (
        <div className="space-y-2">
          {clips.map((clip) => (
            <div
              key={clip.id}
              className="rounded border border-ink-border bg-ink-surface2/50 p-2.5 space-y-2"
              data-testid="audio-clip-card"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">
                  {clip.kind === 'bgm' ? 'BGM' : 'SFX'} ·{' '}
                  {clip.builtinId
                    ? (library?.items.find((i) => i.id === clip.builtinId)?.title ?? clip.builtinId)
                    : (clip.assetId && assets[clip.assetId] ? assetDisplayName(assets[clip.assetId]!) : '未設定')}
                </span>
                <button onClick={() => removeAudio(clip.id)} className="text-text-faint hover:text-accent-red p-1">
                  <Trash2 size={13} />
                </button>
              </div>
              <SliderField
                label="音量"
                value={clip.gainDb}
                min={-40}
                max={6}
                step={1}
                unit="dB"
                onChange={(gainDb) => updateAudio(clip.id, { gainDb })}
              />
              <div className="grid grid-cols-2 gap-2">
                <SliderField
                  label="フェードイン"
                  value={clip.fadeInSec}
                  min={0}
                  max={5}
                  step={0.1}
                  unit="s"
                  onChange={(fadeInSec) => updateAudio(clip.id, { fadeInSec })}
                />
                <SliderField
                  label="フェードアウト"
                  value={clip.fadeOutSec}
                  min={0}
                  max={5}
                  step={0.1}
                  unit="s"
                  onChange={(fadeOutSec) => updateAudio(clip.id, { fadeOutSec })}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* P10-2 担当B §3 item 6: composition.sourceAudio「素材の元音声を残す
          （環境音）」トグル＋ゲイン。render/index.ts が Segment 自身の音声を
          BGM/SFX の下に混ぜる（server/src/render/audio.ts buildAudioGraph の
          sourceAudio param）。 */}
      <div className="rounded border border-ink-border bg-ink-surface2/50 p-2.5 space-y-2" data-testid="source-audio-panel">
        <CheckboxField
          label="素材の元音声を残す（環境音）"
          checked={sourceAudio.enabled}
          onChange={(enabled) => setSourceAudio({ enabled })}
        />
        {sourceAudio.enabled && (
          <SliderField
            label="元音声の音量"
            value={sourceAudio.gainDb}
            min={-30}
            max={0}
            step={0.5}
            unit="dB"
            onChange={(gainDb) => setSourceAudio({ gainDb })}
            data-testid="source-audio-gain"
          />
        )}
      </div>

      <p className="text-[10px] text-text-faint leading-relaxed">
        納品はVO＋SFXのみでBGMを付けない案件もあります（アフィリエイト動画規約）。用途に応じて空のままにしてください。
      </p>
    </div>
  )
}
